import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Sidebar from "../../components/Sidebar";
import {
  Table,
  Button,
  Dropdown,
  Input,
  Select,
  Space,
  Card,
  App,
  Empty,
} from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import theme from "../../themes";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/add.svg";
import CreateCampaignModal from "../../components/CreateCampaignModal";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  getCampaignsThunk,
  updateCampaignThunk,
  deleteCampaignThunk,
} from "../../store/actions/campaign.action";
import { useNavigate } from "react-router-dom";
import { CAMPAIGN_STATUS } from "./type";
import dayjs from "dayjs";
import { ColumnsType } from "antd/es/table";
import { ReactComponent as ArrowUpIcon } from "../../assets/icons/arrowUp.svg";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrowDown.svg";
import ScheduleCampaignModal from "../../components/ScheduleCampaignModal";
import ToastMessage from "../../utils/toastMessage";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";

const { Option } = Select;

const statusColors: Record<string, { text: string; background: string }> = {
  [CAMPAIGN_STATUS.IN_PROGRESS]: {
    text: theme.colors.green1,
    background: theme.colors.green2,
  },
  [CAMPAIGN_STATUS.TEST]: {
    text: theme.colors.yellow2,
    background: theme.colors.yellow3,
  },
  [CAMPAIGN_STATUS.SCHEDULE]: {
    text: theme.colors.blue1,
    background: theme.colors.blue2,
  },
  [CAMPAIGN_STATUS.COMPLETED]: {
    text: theme.colors.purple1,
    background: theme.colors.purple2,
  },
  [CAMPAIGN_STATUS.STOPPED]: {
    text: theme.colors.grey1,
    background: theme.colors.grey5,
  },
};

const Outbound: React.FC = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>(CAMPAIGN_STATUS.ALL);
  const [sorter, setSorter] = useState<{
    field: "createdAt";
    order: "ascend" | "descend";
  }>({ field: "createdAt", order: "descend" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useAppDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const { loading, campaigns } = useAppSelector((state) => state.campaign);
  const navigate = useNavigate();
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
    null
  );
  const { message } = App.useApp();
  const toast = ToastMessage(message);

  const fetchCampaigns = async (page = currentPage) => {
    await dispatch(
      getCampaignsThunk({
        limit: pageSize,
        page,
        keyword: search,
        status: status,
        directionFilter: "",
        sort: {
          [sorter.field]: sorter.order === "descend" ? -1 : 1,
        },
      })
    ).unwrap();
  };

  const handleCustomSort = (field: "createdAt") => {
    let nextOrder: "ascend" | "descend" = "descend";
    if (sorter.field === field) {
      nextOrder = sorter.order === "ascend" ? "descend" : "ascend";
    }
    setSorter({ field, order: nextOrder });
  };

  const columns: ColumnsType<any> = [
    {
      title: t("outbound.campaignName"),
      dataIndex: "campaign_name",
      key: "campaign_name",
      width: "35%",
      render: (text: string, record: any) => (
        <a
          onClick={() => navigate(`/outbound/${record._id}`)}
          style={{
            color: theme.colors.blue1,
            fontWeight: 500,
            textDecoration: "none",
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: (
        <span
          style={{
            cursor: "pointer",
            color: sorter.field === "createdAt" ? "#000" : "inherit",
          }}
          onClick={() => handleCustomSort("createdAt")}
        >
          {t("outbound.createdTime")}{" "}
          {sorter.field === "createdAt" &&
            (sorter.order === "ascend" ? (
              <ArrowUpIcon style={{ marginLeft: 4, verticalAlign: "middle" }} />
            ) : (
              <ArrowDownIcon
                style={{ marginLeft: 4, verticalAlign: "middle" }}
              />
            ))}
        </span>
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      width: "35%",
      render: (createdAt: string) =>
        createdAt ? dayjs(createdAt).format("hh:mm A, MMM DD, YYYY") : "-",
    },
    {
      title: t("outbound.status"),
      dataIndex: "status",
      key: "status",
      width: "20%",
      render: (status: string) => (
        <StatusBadge
          colors={
            statusColors[status] || { text: "#374151", background: "#f3f4f6" }
          }
        >
          {getStatusLabel(status)}
        </StatusBadge>
      ),
    },
    {
      title: "",
      key: "action",
      width: "10%",
      align: "right",
      render: (_: any, record: any) => {
        const canEdit =
          record?.status === CAMPAIGN_STATUS.TEST ||
          record?.status === CAMPAIGN_STATUS.SCHEDULE;
        const items = [
          ...(canEdit ? [{ key: "edit", label: t("outbound.edit") }] : []),
          // { key: "delete", label: "Delete" as const },
        ];
        return (
          <Dropdown
            menu={{
              items,
              onClick: async ({ key }) => {
                if (key === "edit") {
                  setSelectedCampaignId(record._id);
                  setIsScheduleModalOpen(true);
                }
              },
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="text" icon={<EllipsisOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const getStatusLabel = (status: string) => {
    // Handle empty or undefined status
    if (!status) {
      return t("outbound.statusLabels.allStatus");
    }

    const statusMap: Record<string, string> = {
      [CAMPAIGN_STATUS.ALL]: t("outbound.statusLabels.allStatus"),
      [CAMPAIGN_STATUS.SCHEDULE]: t("outbound.statusLabels.scheduled"),
      [CAMPAIGN_STATUS.IN_PROGRESS]: t("outbound.statusLabels.inProgress"),
      [CAMPAIGN_STATUS.STOPPED]: t("outbound.statusLabels.stopped"),
      [CAMPAIGN_STATUS.COMPLETED]: t("outbound.statusLabels.finished"),
      [CAMPAIGN_STATUS.TEST]: t("outbound.statusLabels.testing"),
    };


    return statusMap[status] || status;
  };

  useEffect(() => {
    fetchCampaigns(currentPage);
  }, [currentPage, search, status, sorter]);

  return (
    <FlexRow>
      <MainContent>
        <HeaderRow>
          <Title>{t("outbound.title")}</Title>
          <CreateButton onClick={() => setIsModalOpen(true)}>
            <AddIcon /> {t("outbound.createCampaign")}
          </CreateButton>
        </HeaderRow>
        <CardWrapper>
          <Space
            style={{
              marginBottom: 16,
              width: "100%",
              justifyContent: "space-between",
              display: "flex",
            }}
          >
            <div style={{ display: "flex", gap: 16 }}>
              <SortSelect
                value={status}
                style={{ width: 160 }}
                size="large"
                onChange={(value) => setStatus(value as string)}
              >
                <Option value={CAMPAIGN_STATUS.ALL}>
                  {t("outbound.statusLabels.allStatus")}
                </Option>
                <Option value={CAMPAIGN_STATUS.IN_PROGRESS}>
                  {t("outbound.statusLabels.inProgress")}
                </Option>
                <Option value={CAMPAIGN_STATUS.TEST}>
                  {t("outbound.statusLabels.testing")}
                </Option>
                <Option value={CAMPAIGN_STATUS.SCHEDULE}>
                  {t("outbound.statusLabels.scheduled")}
                </Option>
                <Option value={CAMPAIGN_STATUS.COMPLETED}>
                  {t("outbound.statusLabels.finished")}
                </Option>
                <Option value={CAMPAIGN_STATUS.STOPPED}>
                  {t("outbound.statusLabels.stopped")}
                </Option>
              </SortSelect>
            </div>
            <SearchWrapper>
              <SearchIcon />
              <CustomSearch
                placeholder={t("outbound.searchCampaign")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchWrapper>
          </Space>
          <StyledTable
            dataSource={campaigns.campaigns}
            columns={columns}
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: campaigns.pagination.totalItems,
              position: ["bottomCenter"],
              showSizeChanger: false,
            }}
            onChange={(pagination) => {
              setCurrentPage(pagination.current || 1);
            }}
            rowKey="key"
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <EmptyStateTitle>
                      {t("common.noData")}
                    </EmptyStateTitle>
                  }
                />
              ),
            }}
          />
        </CardWrapper>
        <CreateCampaignModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
        <ScheduleCampaignModal
          open={isScheduleModalOpen}
          onCancel={() => {
            setIsScheduleModalOpen(false);
            setSelectedCampaignId(null);
          }}
          onSchedule={async (utcStartAt: string, tz: string) => {
            if (!selectedCampaignId) return;
            try {
              await dispatch(
                updateCampaignThunk({
                  id: selectedCampaignId,
                  payload: {
                    start_at: utcStartAt,
                    time_zone: tz,
                    status: CAMPAIGN_STATUS.SCHEDULE,
                  },
                })
              ).unwrap();
              toast.success(
                t("outbound.scheduleUpdatedSuccessfully"),
                5,
                <CheckCircleIcon />
              );
              setIsScheduleModalOpen(false);
              setSelectedCampaignId(null);
              await fetchCampaigns();
            } catch (error) {
              toast.error(t("outbound.failedToUpdateSchedule"));
            }
          }}
        />
      </MainContent>
    </FlexRow>
  );
};

const FlexRow = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.grey3};
`;

const MainContent = styled.div`
  flex: 1;
  padding: 52px 72px;
  gap: 32px;
  display: flex;
  flex-direction: column;
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
`;

const CardWrapper = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #047857;
  }
`;

const StatusBadge = styled.span<{
  colors: { text: string; background: string };
}>`
  background: ${({ colors }) => colors.background};
  color: ${({ colors }) => colors.text};
  border-radius: 12px;
  padding: 5px 12px;
  font-size: 14px;
  line-height: 20px;
`;

const SearchWrapper = styled.div`
  width: 240px;
  display: flex;
  background-color: ${theme.colors.grey4};
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  padding: 0px 8px;
  align-items: center;
  box-sizing: border-box;
  gap: 8px;
`;

const CustomSearch = styled(Input)`
  border-radius: 12px;
  height: 40px;
  font-size: 16px;
  border: none;
  box-shadow: none;
  background-color: ${theme.colors.grey4};
  padding: 0;

  &:hover,
  &:focus {
    border: none;
    box-shadow: none;
    background-color: ${theme.colors.grey4};
  }
`;

const SortSelect = styled(Select)`
  .ant-select-selector {
    background: ${theme.colors.grey4} !important;
    border: 1px solid ${theme.colors.grey2} !important;
    box-shadow: none !important;
    outline: none !important;
  }
  .ant-select-selector:focus,
  .ant-select-selector:active {
    box-shadow: none !important;
    outline: none !important;
  }
`;

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: white;
    border-bottom: none;
    padding: 10px 16px;
    font-size: 14px;
    line-height: 20px;
    color: ${theme.colors.grey1};
    font-weight: 500;
  }

  .ant-table-thead > tr > th::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    border-bottom: none;
  }

  .ant-table-tbody > tr:nth-child(odd) {
    background-color: ${theme.colors.grey3};
  }

  .ant-table-tbody > tr:nth-child(even) {
    background-color: ${theme.colors.grey4};
  }

  .ant-table table {
    border-spacing: 0 4px;
    border-collapse: separate;
  }

  .ant-table-tbody > tr > td:last-child {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }
`;

export default Outbound;
